/**
 * Module dependencies.
 */

import { resolveAppUrl } from 'app/core/utils/url-resolver';
import type { DataExport, ExportListResponse } from 'app/types/export';
import type { AxiosInstance } from 'axios';

/**
 * Exports API client.
 */

export const exportsApi = {
  /**
   * List all exports.
   */
  async listExports(client: AxiosInstance, page = 1, pageSize = 20): Promise<ExportListResponse> {
    const url = resolveAppUrl('dataExports', {
      params: { page, pageSize }
    });

    const response = await client.get(url);

    return response.data?.data;
  },

  /**
   * Get export by ID.
   */
  async getExport(client: AxiosInstance, exportId: string): Promise<DataExport> {
    const url = resolveAppUrl('dataExportsById', {
      interpolations: { id: exportId }
    });

    const response = await client.get(url);

    return response.data?.data;
  },

  /**
   * Delete export.
   */
  async deleteExport(client: AxiosInstance, exportId: string): Promise<void> {
    const url = resolveAppUrl('dataExportsDelete', {
      interpolations: { id: exportId }
    });

    await client.delete(url);
  },

  /**
   * Get download URL for export.
   */
  getDownloadUrl(exportId: string): string {
    return resolveAppUrl('dataExportsDownload', {
      interpolations: { id: exportId }
    });
  },

  /**
   * Mark exports as viewed.
   */
  async markExportsViewed(client: AxiosInstance): Promise<void> {
    const url = resolveAppUrl('dataExportsMarkViewed');
    await client.post(url);
  },

  /**
   * Get unseen exports count.
   */
  async getUnseenCount(client: AxiosInstance): Promise<number> {
    const url = resolveAppUrl('dataExportsUnseenCount');
    const response = await client.get(url);
    return response.data?.data?.count ?? response.data?.count ?? 0;
  },

  /**
   * Clear all exports.
   */
  async clearAllExports(client: AxiosInstance): Promise<number> {
    const url = resolveAppUrl('dataExportsClearAll');
    const response = await client.delete(url);
    return response.data?.data?.deleted || 0;
  }
};
